/* «QUESTA CONFIGURAZIONE È GIÀ UN BOT» — il vocabolario, in un posto solo.
 *
 * Tre schermate hanno bisogno di dire la stessa cosa dello stesso bot: la lista
 * dei backtest (questa riga è già in funzione?), il dettaglio di un wallet
 * (quali bot ci operano sopra?) e la schermata Tradebot. Se le tre lo scrivono
 * ognuna a modo suo, prima o poi in una delle tre la parola «REALE» manca — ed è
 * l'unica parola della frase che vale dei soldi.
 *
 * Le tre distinzioni che questo file impone:
 *
 *  1. DENARO VERO O SIMULATO. Sempre a schermo, mai dedotta dal contesto.
 *  2. IN FUNZIONE O FERMO. Un bot fermo esiste ancora: «nessun bot» e «bot
 *     fermo» sono due risposte diverse alla stessa domanda.
 *  3. NON ANCORA LETTO. Finché l'elenco dei bot non è arrivato non si scrive
 *     «nessuno»: non lo sappiamo. Un trattino al posto di un'attesa afferma una
 *     cosa non misurata.
 *
 * Nessun verde e nessun rosso: qui significano guadagno e perdita, e lo stato di
 * un bot non è né l'uno né l'altro. Il denaro vero va in AMBRA — non dice
 * «bene» né «male», dice «attenzione, qui si rischia» — e il simulato in
 * accento, che è il colore del marchio e non un giudizio.
 */

import { useMemo } from 'react';
import { Distintivo } from './Base';
import { tradebots } from '../api/risorse';
import { usaRichiesta, type StatoDati } from '../api/usaRichiesta';
import type { Tradebot } from '../api/tipi';
import type { ErroreApi, ParametriCrud } from '../api/client';
import { chiaveConfig } from '../lib/chiaveConfig';

/* ==========================================================================
 * La proiezione
 * ======================================================================== */

/**
 * Quel poco di un tradebot che basta a dire com'è messo.
 *
 * È una proiezione deliberata, come `WalletPubblico` per i wallet: un
 * componente che riceve `BotCollegato` non PUÒ stampare per sbaglio un token di
 * Telegram o un rpc, perché non ce li ha.
 */
export interface BotCollegato {
  id: number;
  inFunzione: boolean;
  denaroReale: boolean;
}

export function botCollegato(b: Tradebot): BotCollegato {
  return { id: b.id, inFunzione: b.isRunning, denaroReale: b.useRealMoney };
}

/** Le tre parole del capitale e dello stato, in un posto solo: `modalitaBot` in
 *  `pagine/tradebot/modello.ts` le prende da qui, così la lista dei bot e la
 *  lista dei backtest non possono chiamare la stessa cosa in due modi. */
export function etichettaCapitale(denaroReale: boolean): string {
  return denaroReale ? 'REALE' : 'SIMULATO';
}

export function etichettaOperativa(inFunzione: boolean): string {
  return inFunzione ? 'in funzione' : 'fermo';
}

/* ==========================================================================
 * I tre stati che una riga deve distinguere
 * ======================================================================== */

export type ModoBot = 'reale-attivo' | 'simulato-attivo' | 'fermo';

export function modoBot(b: BotCollegato): ModoBot {
  if (!b.inFunzione) return 'fermo';
  return b.denaroReale ? 'reale-attivo' : 'simulato-attivo';
}

interface Aspetto {
  /** Testo del distintivo. Dice la stessa cosa del colore, per chi non
   *  distingue i colori e per chi guarda lo schermo al sole. */
  testo: string;
  tono: 'cautela' | 'accento' | 'neutro';
  /** Pieno quando qualcosa sta girando adesso, vuoto quando è fermo. */
  marcatore: string;
}

const ASPETTI: Record<ModoBot, Aspetto> = {
  'reale-attivo': { testo: 'LIVE · DENARO VERO', tono: 'cautela', marcatore: '◉' },
  'simulato-attivo': { testo: 'LIVE · SIMULATO', tono: 'accento', marcatore: '◉' },
  fermo: { testo: 'BOT FERMO', tono: 'neutro', marcatore: '○' },
};

/**
 * Il modo che vince quando più bot condividono la stessa configurazione.
 *
 * Non è una media: è il caso peggiore. Se fra tre bot uno gira con denaro vero,
 * la riga deve dire «denaro vero» — chi la legge sta decidendo se toccare quella
 * configurazione, e il bot innocuo non rende innocuo quello che non lo è.
 */
export function modoPrevalente(voci: BotCollegato[]): ModoBot | null {
  if (voci.length === 0) return null;
  if (voci.some((b) => b.inFunzione && b.denaroReale)) return 'reale-attivo';
  if (voci.some((b) => b.inFunzione)) return 'simulato-attivo';
  return 'fermo';
}

/** Quanti bot sono nello stato prevalente, e quale è il primo: il distintivo
 *  mostra l'id quando è uno solo — che è il caso normale — e il conteggio
 *  quando sono più di uno, perché allora l'id di uno dei tre non serve. */
function conteggio(voci: BotCollegato[], modo: ModoBot): BotCollegato[] {
  return voci.filter((b) => modoBot(b) === modo);
}

/** Riga per riga, cosa sono questi bot: finisce nel `title`, non SOSTITUISCE
 *  nulla di ciò che è già visibile. */
function dettaglio(voci: BotCollegato[]): string {
  return voci
    .map((b) => `bot #${b.id} · ${etichettaOperativa(b.inFunzione)} · ${b.denaroReale ? 'denaro reale' : 'simulato'}`)
    .join('\n');
}

/* ==========================================================================
 * Il distintivo
 * ======================================================================== */

/**
 * Distintivo compatto per una riga di lista. Una riga sola, `nowrap`: è una
 * lista che si scorre e la riga non deve allungarsi.
 */
export function DistintivoBotInFunzione({ voci }: { voci: BotCollegato[] }) {
  const modo = modoPrevalente(voci);
  if (!modo) return null;

  const aspetto = ASPETTI[modo];
  const dentro = conteggio(voci, modo);
  const coda = dentro.length > 1 ? `×${dentro.length}` : `#${dentro[0].id}`;

  return (
    <span title={dettaglio(voci)}>
      <Distintivo tono={aspetto.tono}>
        <span aria-hidden="true">{aspetto.marcatore}</span>
        {aspetto.testo} {coda}
      </Distintivo>
    </span>
  );
}

/**
 * Le tre risposte possibili in una cella di tabella, compreso il «non lo so
 * ancora». `null` non è `[]`: il primo significa che l'elenco dei bot non è
 * arrivato, il secondo che è arrivato e non contiene nessuno.
 */
export function SegnaleBotInFunzione({ voci }: { voci: BotCollegato[] | null | undefined }) {
  if (voci == null) {
    return (
      <span className="sb-nota" title="L'elenco dei tradebot non è ancora stato letto.">
        …
      </span>
    );
  }
  if (voci.length === 0) {
    return (
      <span className="sb-nota" title="Nessun tradebot gira questa configurazione.">
        —
      </span>
    );
  }
  return <DistintivoBotInFunzione voci={voci} />;
}

/* ==========================================================================
 * L'indice: i bot per configurazione, letti una volta sola
 * ======================================================================== */

/**
 * Colonne che servono a calcolare la chiave e a dire lo stato. Niente altro.
 *
 * `telegramToken` e `telegramPassword` sono deliberatamente fuori: sono
 * credenziali, e una lista che le porta le mette in rete a ogni caricamento di
 * pagina. `strategyParams` invece serve — è il cuore della chiave — e per questo
 * non si può riusare `CAMPI_LISTA_BOT`, che non lo chiede.
 *
 * `trades` arriva comunque: la relazione è EAGER e `fields` non la tocca. È il
 * problema §2 di RICHIESTE-API.md, e qui si paga in banda senza guadagnarci
 * niente — ma i bot sono una dozzina e la richiesta è una sola per pagina.
 */
const CAMPI_CORRELAZIONE: ParametriCrud = {
  fields: [
    'id',
    'isRunning',
    'useRealMoney',
    'strategyName',
    'asset',
    'currency',
    'candleSize',
    'strategyParams',
  ],
  sort: ['id,ASC'],
};

export interface IndiceBot {
  /** `null` finché l'elenco non è arrivato: una mappa vuota direbbe «nessun
   *  bot», e non è la stessa cosa di «non lo so ancora». */
  indice: Map<string, BotCollegato[]> | null;
  stato: StatoDati;
  errore: ErroreApi | null;
  ricarica: () => void;
}

/**
 * I tradebot indicizzati per configurazione.
 *
 * Una richiesta sola per pagina, non una per riga: i bot sono una dozzina e le
 * righe di backtest sono un milione e mezzo. Chi ha la riga calcola la sua
 * chiave con `chiaveConfig` e guarda nella mappa — nessuna chiamata di rete
 * mentre si scorre.
 *
 * `eVuoto` è forzato a `false`: «zero tradebot registrati» è una risposta
 * valida e completa, non un contenitore vuoto da segnalare all'utente. Lo stato
 * `vuoto` farebbe comparire uno stato vuoto in mezzo a una lista di backtest
 * che di righe ne ha.
 */
export function usaBotPerConfigurazione(): IndiceBot {
  const richiesta = usaRichiesta(() => tradebots.elenco(CAMPI_CORRELAZIONE), [], {
    eVuoto: () => false,
  });

  const indice = useMemo(() => {
    if (richiesta.dati == null) return null;
    const m = new Map<string, BotCollegato[]>();
    for (const b of richiesta.dati.data) {
      const k = chiaveConfig(b);
      /* Un bot senza `strategyParams` non è confrontabile con niente: entrare
         nell'indice sotto una chiave inventata lo accoppierebbe a righe che non
         c'entrano. Resta fuori, e la riga del backtest dirà «nessun bot». */
      if (!k) continue;
      const gia = m.get(k);
      if (gia) gia.push(botCollegato(b));
      else m.set(k, [botCollegato(b)]);
    }
    return m;
  }, [richiesta.dati]);

  return {
    indice,
    stato: richiesta.stato,
    errore: richiesta.errore,
    ricarica: richiesta.ricarica,
  };
}
